/* ------------------------------------------------------------------------
   Hero Banner —  sections/hero-banner.liquid.
   ------------------------------------------------------------------------ */

const HB_SLIDE_DURATION = 1000;
const HB_TEXT_DURATION = 800;
const HB_TEXT_DELAY = 600;
const HB_TEXT_STAGGER = 100;
const HB_EASE_IN_OUT = 'cubic-bezier(0.77, 0, 0.175, 1)';
const HB_EASE_OUT = 'cubic-bezier(0.165, 0.84, 0.44, 1)';

class HeroBanner extends HTMLElement {
  constructor() {
    super();

    this.slides = Array.from(this.querySelectorAll('.hb_slide'));
    this.tabs = Array.from(this.querySelectorAll('.hb_thumb'));
    this.tabItems = Array.from(this.querySelectorAll('.hb_thumb_item'));
    this.thumbsList = this.querySelector('.hb_thumbs');
    this.stage = this.querySelector('.hb_stage');
    this.animations = [];
    this.isTransitioning = false;
    this.transitionToken = 0;
    this.transitionTimer = null;
    this.pendingSlides = null;
    this.textAnimations = [];
    this.queued = null;

    const activeSlideIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    this.activeIndex = activeSlideIndex > -1 ? activeSlideIndex : 0;

    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = Number(this.dataset.autoplaySpeed) || 6000;
    this.timer = null;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.inner = null;
    this.parallaxEnabled = this.dataset.parallax === 'true';
    this.scrollRaf = null;
    this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.thumbTrigger = this.dataset.thumbTrigger === 'click' ? 'click' : 'hover';
    this.hoverDebounceTimer = null;
    this.hoverDebounceMs = 80;
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleTabHover = this.handleTabHover.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
    this.pause = this.pause.bind(this);
    this.resume = this.resume.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleIntersect = this.handleIntersect.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.requestScrollUpdate = this.requestScrollUpdate.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.slides.forEach((slide, i) => this.setSlideState(slide, i === this.activeIndex));

    window.addEventListener('resize', this.handleResize);

    // Scroll-driven border-radius (+ optional parallax) on the banner wrapper.
    this.inner = this.closest('.hb_inner');
    if (this.inner) {
      window.addEventListener('scroll', this.requestScrollUpdate, { passive: true });
      window.addEventListener('resize', this.requestScrollUpdate, { passive: true });
      this.onScroll();
    }

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick);
      tab.addEventListener('keydown', this.handleTabKeydown);
      tab.addEventListener('mouseenter', this.handleTabHover);
    });

    if (this.slides.length > 1) {
      this.hoverTarget = this.thumbsList || this;
      this.hoverTarget.addEventListener('mouseenter', this.pause);
      this.hoverTarget.addEventListener('mouseleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.intersectionObserver = new IntersectionObserver(this.handleIntersect, {
        threshold: 0.4,
      });
      this.intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.pause();

    if (this.hoverDebounceTimer) {
      window.clearTimeout(this.hoverDebounceTimer);
      this.hoverDebounceTimer = null;
    }

    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.handleTabClick);
      tab.removeEventListener('keydown', this.handleTabKeydown);
      tab.removeEventListener('mouseenter', this.handleTabHover);
    });

    this.hoverTarget?.removeEventListener('mouseenter', this.pause);
    this.hoverTarget?.removeEventListener('mouseleave', this.resume);

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.intersectionObserver?.disconnect();

    window.removeEventListener('scroll', this.requestScrollUpdate);
    window.removeEventListener('resize', this.requestScrollUpdate);
    window.removeEventListener('resize', this.handleResize);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);

    if (this.transitionTimer) {
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
    this.queued = null;
    this.cancelAnimations();
  }

  handleResize() {
    if (this.isTransitioning && this.pendingSlides) {
      this.finishTransition(this.pendingSlides.oldSlide, this.pendingSlides.newSlide);
    }
  }

  requestScrollUpdate() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(this.onScroll);
  }

  onScroll() {
    this.scrollRaf = null;
    if (!this.inner) return;

    const rect = this.inner.getBoundingClientRect();

   
    const range = window.innerHeight * 0.5;
    const scrolledPast = Math.max(0, -rect.top);
    const progress = range > 0 ? Math.min(1, scrolledPast / range) : 0;

    // Bottom corners animate from 0px to 65px across that progress.
    const radius = progress * 65;
    this.inner.style.borderBottomLeftRadius = `${radius.toFixed(1)}px`;
    this.inner.style.borderBottomRightRadius = `${radius.toFixed(1)}px`;

    if (this.parallaxEnabled && !this.reducedMotionQuery.matches) {
      const offset = progress * (rect.height * 0.06);
      this.style.setProperty('--hb-parallax', `${offset.toFixed(1)}px`);
    }
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.resume();
      } else {
        this.pause();
      }
    });
  }

  handleTabClick(event) {
    const index = Number(event.currentTarget.dataset.index);

    if (this.hoverDebounceTimer) {
      window.clearTimeout(this.hoverDebounceTimer);
      this.hoverDebounceTimer = null;
    }

    if (index === this.activeIndex) {
      this.completeProgress();
      return;
    }

    this.goTo(index, { focusTab: false });
  }

  completeProgress() {
    this.tabItems[this.activeIndex]?.classList.add('is-complete');
  }

  handleTabHover(event) {
    if (this.thumbTrigger !== 'hover') return;
    if (!this.hoverQuery.matches) return;

    const index = Number(event.currentTarget.dataset.index);
    if (index === this.activeIndex) return;

    if (this.hoverDebounceTimer) window.clearTimeout(this.hoverDebounceTimer);
    this.hoverDebounceTimer = window.setTimeout(() => {
      this.hoverDebounceTimer = null;
      this.goTo(index, { focusTab: false });
    }, this.hoverDebounceMs);
  }

  handleTabKeydown(event) {
    const { key } = event;
    const lastIndex = this.tabs.length - 1;
    let targetIndex = null;

    if (key === 'ArrowRight' || key === 'ArrowDown') {
      targetIndex = this.activeIndex === lastIndex ? 0 : this.activeIndex + 1;
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      targetIndex = this.activeIndex === 0 ? lastIndex : this.activeIndex - 1;
    } else if (key === 'Home') {
      targetIndex = 0;
    } else if (key === 'End') {
      targetIndex = lastIndex;
    } else {
      return;
    }

    event.preventDefault();
    this.goTo(targetIndex, { focusTab: true });
  }

  goTo(index, { focusTab = false } = {}) {
    if (!this.slides[index]) return;


    if (this.isTransitioning) {
      this.queued = index === this.activeIndex ? null : { index, focusTab };
      return;
    }

    if (index === this.activeIndex) return;

    const previousIndex = this.activeIndex;
    const forward = index > previousIndex;

    this.activeIndex = index;

    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      if (isActive && focusTab) tab.focus();
    });

    this.tabItems.forEach((item, i) => {
      item.classList.remove('is-complete');
      item.classList.toggle('is-active', i === index);
    });

    this.transition(this.slides[previousIndex], this.slides[index], forward);

    if (this.timer) {
      this.pause();
      this.start();
    }
  }

  setSlideState(slide, active) {
    if (!slide) return;
    slide.classList.toggle('is-active', active);
    slide.setAttribute('aria-hidden', String(!active));
    if ('inert' in slide) slide.inert = !active;
  }

  cancelList(list) {
    list.forEach((animation) => {
      try {
        animation.cancel();
      } catch (error) {
       
      }
    });
    list.length = 0;
  }

  cancelAnimations() {
    this.cancelList(this.animations);
    this.cancelList(this.textAnimations);
  }

  clearSlideStyles(slide) {
    if (!slide) return;

    ['display', 'z-index', 'clip-path', 'will-change', 'width', 'left', 'right'].forEach((prop) => {
      slide.style.removeProperty(prop);
    });

    const media = slide.querySelector('.hb_slide_media');
    if (media) {
      ['transform', 'will-change', 'width', 'left', 'right'].forEach((prop) =>
        media.style.removeProperty(prop),
      );
    }

    const overlay = slide.querySelector('.hb_overlay');
    if (overlay) {
      ['width', 'left', 'right', 'max-width', 'margin'].forEach((prop) =>
        overlay.style.removeProperty(prop),
      );
    }

    slide.querySelectorAll('.hb_content > *').forEach((el) => {
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
    });
  }

  finishTransition(oldSlide, newSlide) {
    if (this.transitionTimer) {
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }

    this.cancelList(this.animations);
    this.setSlideState(newSlide, true);
    this.setSlideState(oldSlide, false);
    this.clearSlideStyles(newSlide);
    this.clearSlideStyles(oldSlide);
    this.pendingSlides = null;
    this.isTransitioning = false;

    const queued = this.queued;
    this.queued = null;
    if (queued && queued.index !== this.activeIndex) {
      this.goTo(queued.index, { focusTab: queued.focusTab });
    }
  }

  transition(oldSlide, newSlide, forward) {
    const stage = this.stage || this;
    const width = stage.clientWidth;
    const canAnimate =
      !this.reducedMotionQuery.matches && typeof newSlide.animate === 'function' && width > 0;

    if (!canAnimate) {
      this.finishTransition(oldSlide, newSlide);
      return;
    }

    const token = ++this.transitionToken;
    this.isTransitioning = true;
    this.pendingSlides = { oldSlide, newSlide };

    if (this.transitionTimer) {
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
    this.cancelAnimations();

    const oldMedia = oldSlide ? oldSlide.querySelector('.hb_slide_media') : null;
    const newMedia = newSlide.querySelector('.hb_slide_media');
    const items = Array.from(newSlide.querySelectorAll('.hb_content > *'));

    
    const clipFrom = forward ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)';
    const clipTo = 'inset(0 0 0 0)';
    const mediaFrom = forward ? width / 8 : -(width / 8);
    const mediaDrift = forward ? -(width / 4) : width / 4;

    newSlide.style.display = 'block';
    newSlide.style.zIndex = '2';
    newSlide.style.clipPath = clipFrom;
    newSlide.style.willChange = 'clip-path';

    if (newMedia) {
      newMedia.style.transform = 'translate3d(' + mediaFrom + 'px, 0, 0)';
      newMedia.style.willChange = 'transform';
    }

    if (oldMedia) {
      oldMedia.style.willChange = 'transform';
    }

    const slideOptions = {
      duration: HB_SLIDE_DURATION,
      easing: HB_EASE_IN_OUT,
      fill: 'forwards',
    };

    this.animations.push(
      newSlide.animate([{ clipPath: clipFrom }, { clipPath: clipTo }], slideOptions),
    );

    if (newMedia) {
      this.animations.push(
        newMedia.animate(
          [
            { transform: 'translate3d(' + mediaFrom + 'px, 0, 0)' },
            { transform: 'translate3d(0, 0, 0)' },
          ],
          slideOptions,
        ),
      );
    }

    if (oldMedia) {
      this.animations.push(
        oldMedia.animate(
          [
            { transform: 'translate3d(0, 0, 0)' },
            { transform: 'translate3d(' + mediaDrift + 'px, 0, 0)' },
          ],
          slideOptions,
        ),
      );
    }

    items.forEach((el, i) => {
      this.textAnimations.push(
        el.animate(
          [
            { opacity: 0, transform: 'translate3d(0, 60px, 0)' },
            { opacity: 1, transform: 'translate3d(0, 0, 0)' },
          ],
          {
            duration: HB_TEXT_DURATION,
            delay: HB_TEXT_DELAY + i * HB_TEXT_STAGGER,
            easing: HB_EASE_OUT,
            fill: 'both',
          },
        ),
      );
    });

   
    this.transitionTimer = window.setTimeout(() => {
      this.transitionTimer = null;
      if (token !== this.transitionToken) return;
      this.finishTransition(oldSlide, newSlide);
    }, HB_SLIDE_DURATION + 30);
  }

  start() {
    if (this.timer || this.slides.length <= 1) return;
    if (!this.autoplay || this.reducedMotionQuery.matches) return;

    this.thumbsList?.classList.remove('is-paused');
    this.restartProgress();

    this.timer = setInterval(() => {
      const nextIndex = (this.activeIndex + 1) % this.slides.length;
      this.goTo(nextIndex);
    }, this.autoplaySpeed);
  }

  pause() {
    if (this.autoplay && !this.reducedMotionQuery.matches) {
      this.thumbsList?.classList.add('is-paused');
    }

    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartProgress() {
    const activeItem = this.tabItems[this.activeIndex];
    if (!activeItem) return;

    activeItem.classList.remove('is-active', 'is-complete');
    void activeItem.offsetWidth;
    activeItem.classList.add('is-active');
  }

  resume() {
    if (document.hidden) return;
    this.start();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else {
      this.resume();
    }
  }
}

if (!customElements.get('hero-banner')) {
  customElements.define('hero-banner', HeroBanner);
}
